import { supabase } from './supabase';

export interface ProfilePictureUploadResult {
  success: boolean;
  url?: string;
  error?: string;
}

export interface ProfilePictureSettings {
  visibility: 'public' | 'private' | 'none';
}

/**
 * Profile Picture Service
 * Handles uploading, updating, and managing profile pictures with privacy controls
 */
export class ProfilePictureService {
  private static readonly BUCKET_NAME = 'profile-pictures';
  private static readonly MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  private static readonly ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

  /**
   * Validate uploaded file
   */
  private static validateFile(file: File): { valid: boolean; error?: string } {
    if (file.size > this.MAX_FILE_SIZE) {
      return { valid: false, error: 'File size must be less than 5MB' };
    }

    if (!this.ALLOWED_TYPES.includes(file.type)) {
      return { valid: false, error: 'File must be JPEG, PNG, WebP, or GIF' };
    }

    return { valid: true };
  }

  /**
   * Generate unique filename for user
   */
  private static generateFileName(userId: string, file: File): string {
    const timestamp = Date.now();
    const extension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    return `${userId}/profile-${timestamp}.${extension}`;
  }

  /**
   * Upload profile picture to Supabase Storage
   */
  static async uploadProfilePicture(
    userId: string,
    file: File,
    visibility: 'public' | 'private' | 'none' = 'private'
  ): Promise<ProfilePictureUploadResult> {
    try {
      console.log('Starting profile picture upload:', { userId, fileName: file.name, fileSize: file.size, visibility });
      
      // Check authentication first
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        console.error('Authentication error:', authError);
        return { success: false, error: 'User not authenticated' };
      }
      
      if (user.id !== userId) {
        console.error('User ID mismatch:', { currentUser: user.id, requestedUser: userId });
        return { success: false, error: 'Cannot upload picture for another user' };
      }

      // Validate file
      const validation = this.validateFile(file);
      if (!validation.valid) {
        console.error('File validation failed:', validation.error);
        return { success: false, error: validation.error };
      }

      // Delete existing profile picture first
      console.log('Deleting existing profile picture...');
      await this.deleteExistingProfilePicture(userId);

      // Generate filename
      const fileName = this.generateFileName(userId, file);
      console.log('Generated filename:', fileName);

      // Upload to Supabase Storage with additional options
      console.log('Uploading to Supabase Storage...');
      console.log('Using bucket name:', this.BUCKET_NAME);
      console.log('Full upload path:', `${this.BUCKET_NAME}/${fileName}`);
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from(this.BUCKET_NAME)
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
          duplex: 'half'
        });

      if (uploadError) {
        console.error('Upload error details:', {
          message: uploadError.message,
          statusCode: (uploadError as any).statusCode,
          error: uploadError
        });
        return { success: false, error: `Upload failed: ${uploadError.message}` };
      }

      console.log('Upload successful:', uploadData);

      // Try to get a signed URL first (better for private buckets)
      const { data: signedUrlData, error: signedUrlError } = await supabase.storage
        .from(this.BUCKET_NAME)
        .createSignedUrl(fileName, 60 * 60 * 24 * 365); // 1 year expiry

      let imageUrl: string;
      
      if (signedUrlError || !signedUrlData) {
        console.warn('Failed to create signed URL, falling back to public URL:', signedUrlError);
        // Fallback to public URL
        const { data: { publicUrl } } = supabase.storage
          .from(this.BUCKET_NAME)
          .getPublicUrl(fileName);
        imageUrl = publicUrl;
        console.log('Generated public URL:', imageUrl);
      } else {
        imageUrl = signedUrlData.signedUrl;
        console.log('Generated signed URL:', imageUrl);
      }

      // Update user profile with new picture URL and visibility
      console.log('Updating profile in database...');
      const { data: updateData, error: updateError } = await supabase
        .from('profiles')
        .update({
          profile_picture_url: imageUrl,
          profile_picture_visibility: visibility
        })
        .eq('id', userId)
        .select();

      if (updateError) {
        console.error('Profile update error:', updateError);
        // Clean up uploaded file if profile update fails
        console.log('Cleaning up uploaded file due to profile update failure...');
        await supabase.storage
          .from(this.BUCKET_NAME)
          .remove([fileName]);
        
        return { success: false, error: `Failed to update profile: ${updateError.message}` };
      }

      console.log('Profile updated successfully:', updateData);
      return { success: true, url: imageUrl };
    } catch (error) {
      console.error('Upload profile picture error:', error);
      return { success: false, error: 'Failed to upload profile picture' };
    }
  }

  /**
   * Update profile picture visibility setting
   */
  static async updateProfilePictureVisibility(
    userId: string,
    visibility: 'public' | 'private' | 'none'
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ profile_picture_visibility: visibility })
        .eq('id', userId);

      if (error) {
        console.error('Update visibility error:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Update profile picture visibility error:', error);
      return { success: false, error: 'Failed to update visibility setting' };
    }
  }

  /**
   * Delete profile picture
   */
  static async deleteProfilePicture(userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      // Get current profile picture URL
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('profile_picture_url')
        .eq('id', userId)
        .single();

      if (profileError) {
        console.error('Get profile error:', profileError);
        return { success: false, error: profileError.message };
      }

      // Delete from storage if exists
      if (profile?.profile_picture_url) {
        await this.deleteExistingProfilePicture(userId);
      }

      // Update profile to remove picture URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          profile_picture_url: null,
          profile_picture_visibility: 'none'
        })
        .eq('id', userId);

      if (updateError) {
        console.error('Update profile error:', updateError);
        return { success: false, error: updateError.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Delete profile picture error:', error);
      return { success: false, error: 'Failed to delete profile picture' };
    }
  }

  /**
   * Delete existing profile picture from storage
   */
  private static async deleteExistingProfilePicture(userId: string): Promise<void> {
    try {
      // List all files for the user
      const { data: files, error: listError } = await supabase.storage
        .from(this.BUCKET_NAME)
        .list(userId);

      if (listError) {
        console.warn('Error listing files for deletion:', listError);
        return;
      }

      // Delete all files for the user (in case there are multiple)
      if (files && files.length > 0) {
        const filePaths = files.map(file => `${userId}/${file.name}`);
        const { error: deleteError } = await supabase.storage
          .from(this.BUCKET_NAME)
          .remove(filePaths);

        if (deleteError) {
          console.warn('Error deleting existing files:', deleteError);
        }
      }
    } catch (error) {
      console.warn('Error in deleteExistingProfilePicture:', error);
    }
  }

  /**
   * Get profile picture URL with privacy check (client-side helper)
   */
  static getProfilePictureUrl(
    profilePictureUrl?: string,
    visibility?: 'public' | 'private' | 'none',
    pictureOwnerId?: string,
    currentUserId?: string,
    hasCoachRelationship?: boolean
  ): string | null {
    if (!profilePictureUrl || !visibility || visibility === 'none') {
      return null;
    }

    if (visibility === 'public') {
      return profilePictureUrl;
    }

    if (visibility === 'private') {
      // Owner can always see their own picture
      if (currentUserId === pictureOwnerId) {
        return profilePictureUrl;
      }

      // Coach-coachee relationship allows viewing
      if (hasCoachRelationship) {
        return profilePictureUrl;
      }
    }

    return null;
  }
} 